import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import Papa from 'papaparse';
import { verifyEmails } from './EmailVerification';
import { extractLinkedInUsername } from '../services/linkedinScraper';

const FileUpload = ({ onFileUpload, disabled, verifyEmails = false, apiKey = null, onEmailsExtracted = null, scrapingOptions = { scrapeWebsite: true, scrapeLinkedIn: false, scrapeTalkingPoints: false } }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  const handleDrag = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    if (disabled) return;
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handlePaste = (e) => {
    if (disabled) return;
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    
    if (text) {
      // Parse the pasted text as CSV
      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          processResults(results);
        },
        error: (error) => {
          console.error('CSV parsing error:', error);
          alert('Error parsing pasted data. Please check the format.');
        }
      });
    }
  };

  const handleFile = (file) => {
    console.log('🔍 FileUpload: handleFile called with file:', file?.name);
    console.log('🔍 FileUpload: verifyEmails mode:', verifyEmails);
    console.log('🔍 FileUpload: onEmailsExtracted callback:', !!onEmailsExtracted);
    
    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please upload a CSV file.');
      return;
    }
    
    setUploadedFile(file);
    console.log('Starting CSV parsing...');
    
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        console.log('CSV parsing complete:', results);
        processResults(results);
      },
      error: (error) => {
        console.error('CSV parsing error:', error);
        alert('Error parsing CSV file. Please check the format.');
      }
    });
  };

  const processResults = (results) => {
    console.log('🔍 FileUpload: processResults called');
    console.log('📊 Results data length:', results?.data?.length || 0);
    console.log('🔍 Verify emails mode:', verifyEmails);
    console.log('🔍 onEmailsExtracted available:', !!onEmailsExtracted);
    
    if (verifyEmails) {
      // Extract emails for verification
      const emails = results.data
        .map(row => {
          const email = row.work_email || row.email || row.Email || '';
          return email.trim();
        })
        .filter(email => email !== '');
      
      if (emails.length === 0) {
        alert('No email addresses found in the CSV file.');
        return;
      }
      
      // Store the raw data for later use
      const rawData = results.data.map(row => {
        // Get website URL
        const url = row.company_website || row.website || row.url || row.Website || row.URL || '';
        
        // Get company name
        const companyName = row.company || row.Company || row.company_name || row.name || row.Name || row.organization || row.Organization || '';
        
        // Get email address
        const email = row.work_email || row.email || row.Email || '';
        
        // Get person's name
        const firstName = row.first_name || row.firstName || row.fname || '';
        const lastName = row.last_name || row.lastName || row.lname || '';
        
        // Get job title
        const jobTitle = row.job_title || row.jobTitle || row.title || row.position || '';
        
        // Get LinkedIn URLs
        const companyLinkedIn = row.company_linkedin || row.companyLinkedIn || '';
        const personLinkedIn = row.linkedin || row.LinkedIn || '';
        
        // Extract LinkedIn username from person LinkedIn URL
        const linkedinUsername = personLinkedIn ? extractLinkedInUsername(personLinkedIn) : '';
        
        // Get additional fields
        const country = row.country || row.Country || '';
        const industry = row.industry || row.Industry || '';
        const phoneNumber = row.phone_number || row.phoneNumber || row.phone || '';

        return {
            url: url.trim(),
            company: companyName.trim(),
            email: email.trim(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            jobTitle: jobTitle.trim(),
            companyLinkedIn: companyLinkedIn.trim(),
            personLinkedIn: personLinkedIn.trim(),
            linkedinUrl: personLinkedIn.trim(),
            linkedinUsername: linkedinUsername || '',
            country: country.trim(),
            industry: industry.trim(),
            phoneNumber: phoneNumber.trim(),
            rawData: row
          };
      });
      
      // Pass emails and raw data to parent component
      console.log('🔍 FileUpload: About to call onEmailsExtracted');
      console.log('📧 Emails found:', emails.length);
      console.log('📊 Raw data rows:', rawData.length);
      console.log('🔗 onEmailsExtracted callback exists:', !!onEmailsExtracted);
      
      if (onEmailsExtracted) {
        console.log('📤 Calling onEmailsExtracted callback...');
        onEmailsExtracted(emails, rawData);
        console.log('✅ onEmailsExtracted callback completed');
      } else {
        console.warn('⚠️ onEmailsExtracted callback is not provided!');
      }
    } else {
      // Original website processing logic
      const websites = results.data
        .filter(row => {
          // Look for company website column - could be named differently
          const websiteUrl = row.company_website || row.website || row.url || row.Website || row.URL || '';
          return websiteUrl && websiteUrl.trim();
        })
        .map((row) => {
          // Get website URL
          const url = row.company_website || row.website || row.url || row.Website || row.URL || '';
          
          // Get company name
          const companyName = row.company || row.Company || row.company_name || row.name || row.Name || row.organization || row.Organization || '';
          
          // Get email address
          const email = row.work_email || row.email || row.Email || '';
          
          // Get person's name
          const firstName = row.first_name || row.firstName || row.fname || '';
          const lastName = row.last_name || row.lastName || row.lname || '';
          
          // Get job title
          const jobTitle = row.job_title || row.jobTitle || row.title || row.position || '';
          
          // Get LinkedIn URLs
          const companyLinkedIn = row.company_linkedin || row.companyLinkedIn || '';
          const personLinkedIn = row.linkedin || row.LinkedIn || '';
          
          // Extract LinkedIn username from person LinkedIn URL
          const linkedinUsername = personLinkedIn ? extractLinkedInUsername(personLinkedIn) : '';
          
          // Get additional fields
          const country = row.country || row.Country || '';
          const industry = row.industry || row.Industry || '';
          const phoneNumber = row.phone_number || row.phoneNumber || row.phone || '';

          return {
            url: url.trim(),
            company: companyName.trim(),
            email: email.trim(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            jobTitle: jobTitle.trim(),
            companyLinkedIn: companyLinkedIn.trim(),
            personLinkedIn: personLinkedIn.trim(),
            linkedinUrl: personLinkedIn.trim(),
            linkedinUsername: linkedinUsername || '',
            country: country.trim(),
            industry: industry.trim(),
            phoneNumber: phoneNumber.trim(),
            // Add raw data for potential use later
            rawData: row
          };
        });

      console.log('Calling onFileUpload with websites:', websites);
      onFileUpload(websites);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.1 }}
      className="upload-card"
    >
      <div className="upload-header">
        <SafeIcon icon="FiUploadCloud" />
        <h3 className="upload-title">
          {verifyEmails ? 'Upload CSV with Emails to Verify' : 'Upload or Paste CSV'}
        </h3>
      </div>
      <div
        className={`upload-area ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        style={{ opacity: disabled ? 0.6 : 1 }}
      >
        <SafeIcon icon="FiUploadCloud" className="upload-icon" />
        {!uploadedFile ? (
          <>
            <p className="upload-text">
              Drag and drop your CSV file here, click to browse, or paste data
            </p>
            <p className="upload-hint">
              {verifyEmails 
                ? `CSV should include a column for email addresses (work_email, email, or Email)${scrapingOptions.scrapeLinkedIn ? ' and linkedin for LinkedIn URLs' : ''}` 
                : `CSV should include columns for work_email, company, job_title${scrapingOptions.scrapeWebsite ? ', company_website' : ''}${scrapingOptions.scrapeLinkedIn ? ', linkedin (for LinkedIn URLs)' : ''}${scrapingOptions.scrapeTalkingPoints ? ', company_website (for talking points)' : ''}, country, first_name, last_name, industry, phone_number`}
            </p>
            <input
              type="file"
              accept=".csv"
              onChange={handleChange}
              style={{ display: 'none' }}
              id="file-upload"
              disabled={disabled}
            />
            <label
              htmlFor="file-upload"
              className="upload-button"
              style={{ pointerEvents: disabled ? 'none' : 'auto' }}
            >
              Choose File
            </label>
            <textarea 
              className="paste-area" 
              placeholder="Or paste CSV data here..."
              onPaste={handlePaste}
              disabled={disabled}
            ></textarea>
          </>
        ) : (
          <p className="upload-text" style={{ color: '#4f46e5' }}>
            <strong>{uploadedFile.name}</strong> uploaded successfully
          </p>
        )}
      </div>
      <style jsx="true">{`
        .upload-hint {
          font-size: 0.75rem;
          color: var(--text-light);
          margin-bottom: 1rem;
        }
        
        .paste-area {
          width: 100%;
          margin-top: 1rem;
          padding: 0.75rem;
          border: 1px dashed #e5e7eb;
          border-radius: 8px;
          height: 100px;
          resize: none;
          font-size: 0.875rem;
          color: var(--text-light);
        }
        
        .paste-area:focus {
          outline: none;
          border-color: var(--primary-color);
        }
      `}</style>
    </motion.div>
  );
};

export default FileUpload;